import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
    MdDashboard, 
    MdShoppingCart, 
    MdInventory, 
    MdAnalytics, 
    MdAccountBalanceWallet, 
    MdNotifications, 
    MdSupportAgent, 
    MdAccountBalance, 
    MdFormatSize, 
    MdPerson,
    MdMenuOpen,
    MdLogout,
    MdWbSunny,
    MdDarkMode
} from 'react-icons/md';
import { RiLayoutGridLine } from 'react-icons/ri';

function AdminLayout({ children }) {
    const [collapsed, setCollapsed] = useState(false);
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
    const location = useLocation();
    const { user, logout } = useAuth();

    const navItems = [
        { to: '/', label: 'Dashboard', icon: <MdDashboard size={20} /> },
        { to: '/orders', label: 'Orders', icon: <MdShoppingCart size={20} /> },
        { to: '/products', label: 'Products', icon: <MdInventory size={20} /> },
        { to: '/products/reorder', label: 'Arrange Products', icon: <RiLayoutGridLine size={20} /> },
        { to: '/stats', label: 'Statistics', icon: <MdAnalytics size={20} /> },
        { to: '/settlements', label: 'Settlements', icon: <MdAccountBalanceWallet size={20} /> },
        { to: '/notifications', label: 'Notifications', icon: <MdNotifications size={20} /> },
        { to: '/support', label: 'Support Inquiries', icon: <MdSupportAgent size={20} /> },
        { to: '/bank-details', label: 'Bank Details', icon: <MdAccountBalance size={20} /> },
        { to: '/size-charts', label: 'Size Charts', icon: <MdFormatSize size={20} /> },
        { to: '/profile', label: 'Edit Profile', icon: <MdPerson size={20} /> }
    ];

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    const activeItem = navItems.find(n => location.pathname === n.to || (n.to === '/' && location.pathname === '/')) || navItems[0];

    return (
        <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', transition: 'background 0.3s ease' }}>
            {/* Sidebar */}
            <aside style={{ 
                width: collapsed ? 80 : 260, 
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                background: '#0f172a', 
                color: 'white', 
                position: 'sticky', 
                top: 0, 
                height: '100vh',
                zIndex: 50,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '10px 0 30px rgba(0,0,0,0.1)'
            }}>
                <div style={{ padding: '24px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ 
                        width: 40, 
                        height: 40, 
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', 
                        borderRadius: 12, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: 20,
                        fontWeight: 900,
                        color: 'white',
                        flexShrink: 0
                    }}>
                        I
                    </div>
                    {!collapsed && (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 800, fontSize: 16, tracking: '-0.02em' }}>Ithyaraa</span>
                            <span style={{ fontSize: 10, opacity: 0.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Brand Portal</span>
                        </div>
                    )}
                </div>

                <nav style={{ padding: 12, flex: 1, overflowY: 'auto' }}>
                    {navItems.map((n) => {
                        const active = location.pathname === n.to || (n.to === '/' && location.pathname === '/');
                        return (
                            <Link 
                                key={n.to} 
                                to={n.to} 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 12, 
                                    padding: '12px 14px', 
                                    margin: '4px 0', 
                                    borderRadius: 12, 
                                    color: active ? 'white' : '#94a3b8', 
                                    textDecoration: 'none', 
                                    background: active ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                    border: active ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid transparent',
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                className="group"
                            >
                                {active && (
                                    <div style={{ position: 'absolute', left: 0, top: '20%', bottom: '20%', width: 4, background: '#3b82f6', borderRadius: '0 4px 4px 0' }} />
                                )}
                                <span style={{ color: active ? '#3b82f6' : 'inherit' }}>{n.icon}</span>
                                {!collapsed && <span style={{ fontSize: 14, fontWeight: active ? 600 : 500 }}>{n.label}</span>}
                            </Link>
                        );
                    })}
                </nav>

                <div style={{ padding: 12, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <button 
                        onClick={() => setCollapsed(!collapsed)} 
                        style={{ 
                            width: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            gap: 12,
                            padding: '12px 14px',
                            background: 'transparent', 
                            border: 'none', 
                            color: '#94a3b8', 
                            cursor: 'pointer',
                            borderRadius: 12,
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                        <MdMenuOpen size={20} style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
                        {!collapsed && <span style={{ fontSize: 14, fontWeight: 600 }}>Collapse Menu</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content Area */}
            <main style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <header style={{ 
                    height: 72, 
                    background: theme === 'dark' ? 'rgba(11, 18, 32, 0.8)' : 'rgba(255, 255, 255, 0.8)', 
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderBottom: '1px solid var(--border)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    padding: '0 32px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 40
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, tracking: '-0.02em', color: 'var(--text)' }}>
                                {activeItem.label}
                            </h2>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Brand Portal <span style={{ opacity: 0.3 }}>/</span> {activeItem.label}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        {/* Theme Toggle */}
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', 
                            padding: 4, 
                            borderRadius: 12,
                            border: '1px solid var(--border)'
                        }}>
                            <button 
                                onClick={() => setTheme('light')}
                                style={{ 
                                    padding: '6px 8px', 
                                    border: 'none', 
                                    borderRadius: 8, 
                                    background: theme === 'light' ? 'var(--surface)' : 'transparent',
                                    color: theme === 'light' ? '#f59e0b' : 'var(--muted)',
                                    cursor: 'pointer',
                                    boxShadow: theme === 'light' ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <MdWbSunny size={18} />
                            </button>
                            <button 
                                onClick={() => setTheme('dark')}
                                style={{ 
                                    padding: '6px 8px', 
                                    border: 'none', 
                                    borderRadius: 8, 
                                    background: theme === 'dark' ? 'var(--surface)' : 'transparent',
                                    color: theme === 'dark' ? '#3b82f6' : 'var(--muted)',
                                    cursor: 'pointer',
                                    boxShadow: theme === 'dark' ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <MdDarkMode size={18} />
                            </button>
                        </div>

                        {/* User Profile & Logout */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingLeft: 24, borderLeft: '1px solid var(--border)' }}>
                            {user && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                                        {user.name || user.username}
                                    </span>
                                    <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>
                                        Brand Account
                                    </span>
                                </div>
                            )}
                            <button
                                onClick={logout}
                                style={{
                                    width: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#ef4444',
                                    border: 'none',
                                    borderRadius: 12,
                                    color: 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                title="Sign Out"
                            >
                                <MdLogout size={20} />
                            </button>
                        </div>
                    </div>
                </header>

                <div style={{ padding: '32px', flex: 1 }}>
                    {children}
                </div>
            </main>
        </div>
    );
}

export default AdminLayout;


